import { ArrowSquareOutIcon, CaretDownIcon, WarningCircleIcon } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { StatusBadge, type StatusTone } from '@/components/ui/status-badge';
import type {
  FactCheckClaim,
  FactCheckReportSource,
  StoredFactCheck,
} from '@/lib/content-doctrine/article-factcheck';
import { DISPUTED_BETWEEN_RUNS } from '@/lib/content-doctrine/article-factcheck-stability';
import { formatWhen } from '@/lib/format-when';
import { COLLAPSIBLE_MOTION, ITEM_META, SECTION_LABEL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** The record's outcome as one badge: failed (wrong law) and needs review (a person must look) never share a tone. */
export function factCheckOutcome({ report, isCurrent }: StoredFactCheck): {
  label: string;
  tone: StatusTone;
} {
  if (!isCurrent) return { label: 'Earlier version', tone: 'neutral' };
  switch (report.status) {
    case 'failed':
      return { label: 'Failed', tone: 'destructive' };
    case 'needs_review':
      return { label: 'Needs review', tone: 'warning' };
    case 'nothing_to_check':
      return { label: 'Nothing to check', tone: 'neutral' };
    default:
      return { label: 'Passed', tone: 'success' };
  }
}

function needsReviewClause(counts: StoredFactCheck['report']['counts']): string | null {
  const unverifiable = Math.max(0, counts.unverifiable - counts.informational);
  const parts = [
    counts.unsupported
      ? `${plural(counts.unsupported, 'claim is', 'claims are')} not stated by the sections the article cites`
      : null,
    unverifiable
      ? `${plural(unverifiable, 'claim could', 'claims could')} not be verified against the law`
      : null,
  ].filter(Boolean);
  return parts.length ? parts.join('; ') : null;
}

/** One sentence that says what the check found, never more than the record supports. */
export function factCheckSummary({ report, isCurrent }: StoredFactCheck): string {
  const { counts } = report;
  if (!isCurrent)
    return 'This check was for an earlier version of the article. Check the claims again before relying on it.';
  if (report.status === 'nothing_to_check')
    return counts.informational
      ? 'Nothing to check: the article cites whole chapters, not sections, so there was no provision to read it against.'
      : 'Nothing to check: no statute sections are cited in this article, so there was no cited law to read it against.';
  if (report.passed) return 'Every cited statute was read and matched what the article says.';
  if (report.status === 'failed') {
    const wrong = [
      counts.contradicted
        ? `${plural(counts.contradicted, 'claim is', 'claims are')} contradicted by the law the article cites`
        : null,
      counts.misapplied
        ? `${plural(counts.misapplied, 'claim applies', 'claims apply')} a provision beyond its scope`
        : null,
    ]
      .filter(Boolean)
      .join('; ');
    const rest = needsReviewClause(counts);
    return `Failed: ${wrong}.${rest ? ` Also needs review: ${rest}.` : ''} The article has not been changed.`;
  }
  return `Needs review: ${needsReviewClause(counts) ?? 'a claim is not fully supported'}. Nothing is shown to misstate the law. The article has not been changed.`;
}

function countsLine({ report }: StoredFactCheck): string {
  const { counts } = report;
  const checked = counts.total - counts.informational;
  const unverifiable = Math.max(0, counts.unverifiable - counts.informational);
  return [
    plural(checked, 'claim checked', 'claims checked'),
    counts.supported ? `${counts.supported} supported` : null,
    counts.contradicted ? `${counts.contradicted} contradicted` : null,
    counts.misapplied ? `${counts.misapplied} outside the provision's scope` : null,
    counts.unsupported ? `${counts.unsupported} not stated by the law` : null,
    unverifiable ? `${unverifiable} could not be verified` : null,
    counts.notApplicable
      ? `${plural(counts.notApplicable, 'date or event', 'dates and events')} not checked against a statute`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

const FINDING_LABEL: Partial<Record<FactCheckClaim['verdict'], string>> = {
  contradicted: 'Contradicted',
  misapplied: "Outside the provision's scope",
  unsupported: 'Not stated',
};

function FactCheckFinding({ claim }: { claim: FactCheckClaim }): JSX.Element {
  const fails = claim.verdict === 'contradicted' || claim.verdict === 'misapplied';
  const heading = [
    FINDING_LABEL[claim.verdict] ?? 'Not stated',
    claim.kind === 'uncited_rule' ? 'uncited rule' : null,
    claim.citation,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <li className="flex min-w-0 items-start gap-2">
      <WarningCircleIcon
        weight="regular"
        className={cn('mt-0.5 size-3.5 shrink-0', fails ? 'text-destructive' : 'text-warning')}
        aria-hidden
      />
      <div className="min-w-0 max-w-full space-y-1.5">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <p className="break-words text-caption font-medium text-foreground">{heading}</p>
          {claim.disputed ? (
            <StatusBadge tone="warning" fill="outline">
              {DISPUTED_BETWEEN_RUNS}
            </StatusBadge>
          ) : null}
        </div>
        <p className="break-words text-caption text-foreground/90">{claim.text}</p>
        {claim.quote ? (
          <blockquote className="min-w-0 max-w-full break-words border-l-2 border-border pl-3 text-caption leading-relaxed">
            {claim.quote}
          </blockquote>
        ) : null}
        {claim.extraQuote ? (
          <blockquote className="min-w-0 max-w-full break-words border-l-2 border-border pl-3 text-caption leading-relaxed">
            {claim.extraQuote}
          </blockquote>
        ) : null}
        {claim.correction ? (
          <p className="break-words text-caption">
            <span className="text-muted-foreground">
              {claim.verdict === 'misapplied'
                ? 'Its scope: '
                : claim.attribution
                  ? 'The page says: '
                  : 'The law says: '}
            </span>
            {claim.correction}
          </p>
        ) : null}
        {claim.reason ? (
          <p className="break-words text-caption">
            <span className="text-muted-foreground">Why: </span>
            {claim.reason}
          </p>
        ) : null}
        {claim.note && (claim.disputed || (!claim.quote && !claim.correction && !claim.reason)) ? (
          <p className={cn(ITEM_META, 'break-words')}>{claim.note}</p>
        ) : null}
        {claim.sourceUrl ? (
          <a
            href={claim.sourceUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex w-fit items-center gap-1 whitespace-nowrap text-caption text-muted-foreground underline decoration-border underline-offset-2 hover:decoration-foreground"
          >
            Read the source · {hostOf(claim.sourceUrl)}
            <ArrowSquareOutIcon weight="regular" className="size-3" aria-hidden />
          </a>
        ) : null}
      </div>
    </li>
  );
}

const ORIGIN_LABEL = {
  cited: 'cited in the article',
  authority: 'looked up for an uncited rule',
  companion: 'read alongside a cited section',
} as const;

function WhatWasRead({ sources }: { sources: FactCheckReportSource[] }): JSX.Element {
  return (
    <ul className="min-w-0 space-y-3">
      {sources.map((source) => (
        <li key={source.url} className="min-w-0 space-y-1 text-caption">
          <a
            href={source.url}
            target="_blank"
            rel="noreferrer noopener"
            className="break-all text-foreground/90 underline decoration-border underline-offset-2 hover:decoration-foreground"
          >
            {source.url.replace(/^https?:\/\//, '')}
          </a>
          {!source.fetchedOk ? (
            <span className="block text-muted-foreground">
              Could not be read{source.note ? `: ${source.note}` : ''}
            </span>
          ) : null}
          {(source.reads ?? []).map((read) => (
            <div key={`${source.url}-${read.section ?? 'chapter'}`} className="min-w-0 pl-3">
              <p className="break-words text-foreground/90">
                {read.section ? `§ ${read.section}` : `Chapter ${source.chapter}`}
                {read.heading ? ` · ${read.heading}` : ''}
              </p>
              <p className={cn(ITEM_META, 'break-words')}>
                {read.heading === null
                  ? 'Not found on the page'
                  : `${read.chars.toLocaleString('en-US')} characters read · ${ORIGIN_LABEL[read.origin ?? 'cited']}`}
              </p>
              {read.excerpt ? (
                <Collapsible>
                  <CollapsibleTrigger asChild>
                    <Button variant="flat" size="sm" className="group -ml-2">
                      <CaretDownIcon className="transition-transform group-data-[state=closed]:-rotate-90" />
                      Show the passage
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className={cn(COLLAPSIBLE_MOTION, 'min-w-0 max-w-full pt-1')}>
                    <blockquote className="min-w-0 max-w-full break-words border-l-2 border-border pl-3 text-caption leading-relaxed">
                      {read.excerpt}
                      {read.chars > read.excerpt.length ? ' …' : ''}
                    </blockquote>
                  </CollapsibleContent>
                </Collapsible>
              ) : null}
            </div>
          ))}
          {!(source.reads ?? []).length && source.fetchedOk ? (
            <span className="block text-muted-foreground">
              {source.sections.length
                ? `Read · ${source.sections.map((section) => `§ ${section}`).join(', ')}`
                : 'Read'}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/**
 * The claim check on file for an article: what was compared with fetched law,
 * and which claims did not match. A record of what was found. It never edits the
 * article. Claims that misstate the law (errors) fail the check and block a new
 * hand-off; claims the law does not state or that could not be verified
 * (warnings) ask for review and do not.
 */
export function EditorialFactCheck({ factCheck }: { factCheck: StoredFactCheck }): JSX.Element {
  const { report, isCurrent } = factCheck;
  const isError = (claim: FactCheckClaim) =>
    claim.verdict === 'contradicted' || claim.verdict === 'misapplied';
  const errors = report.claims.filter((claim) => !claim.informational && isError(claim));
  const warnings = report.claims.filter(
    (claim) => !claim.informational && claim.verdict === 'unsupported',
  );
  const unverifiable = report.claims.filter(
    (claim) => !claim.informational && claim.verdict === 'unverifiable',
  );
  const outcome = factCheckOutcome(factCheck);
  const informational = report.claims.filter((claim) => claim.informational);
  return (
    <section className="min-w-0 max-w-full space-y-2 overflow-x-hidden">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <p className={SECTION_LABEL}>Claims checked against sources</p>
        <StatusBadge tone={outcome.tone} fill="outline">
          {outcome.label}
        </StatusBadge>
      </div>
      <p className={ITEM_META}>
        {factCheckSummary(factCheck)} Checked {formatWhen(report.checkedAt)}.
      </p>
      {report.counts.total - report.counts.informational > 0 ? (
        <p className={ITEM_META}>{countsLine(factCheck)}</p>
      ) : null}
      {errors.length || warnings.length ? (
        <ul className={cn('min-w-0 space-y-3', !isCurrent && 'opacity-70')}>
          {[...errors, ...warnings].map((claim) => (
            <FactCheckFinding key={claim.id} claim={claim} />
          ))}
        </ul>
      ) : null}
      {unverifiable.length ? (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <Button variant="flat" size="sm" className="group -ml-2">
              <CaretDownIcon className="transition-transform group-data-[state=closed]:-rotate-90" />
              <WarningCircleIcon weight="regular" className="text-warning" aria-hidden />
              Could not verify · {unverifiable.length}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent
            className={cn(COLLAPSIBLE_MOTION, 'min-w-0 max-w-full space-y-3 pt-2')}
          >
            {unverifiable.map((claim) => (
              <div key={claim.id} className="min-w-0 space-y-0.5">
                <p className="break-words text-caption text-foreground/90">{claim.text}</p>
                {claim.reason ? (
                  <p className={cn(ITEM_META, 'break-words')}>{claim.reason}</p>
                ) : null}
                {claim.note && claim.note !== claim.reason ? (
                  <p className={cn(ITEM_META, 'break-words')}>{claim.note}</p>
                ) : null}
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      ) : null}
      {informational.length ? (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <Button variant="flat" size="sm" className="group -ml-2">
              <CaretDownIcon className="transition-transform group-data-[state=closed]:-rotate-90" />
              Chapter citations, for information · {informational.length}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent
            className={cn(COLLAPSIBLE_MOTION, 'min-w-0 max-w-full space-y-3 pt-2')}
          >
            <p className={cn(ITEM_META, 'break-words')}>
              These cite a whole chapter with no section, so they are not checked and do not affect
              the result.
            </p>
            {informational.map((claim) => (
              <div key={claim.id} className="min-w-0 space-y-0.5">
                <p className="break-words text-caption text-foreground/90">{claim.text}</p>
                {claim.citation ? (
                  <p className={cn(ITEM_META, 'break-words')}>{claim.citation}</p>
                ) : null}
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      ) : null}
      {report.sources.length || report.limitations.length ? (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <Button variant="flat" size="sm" className="group -ml-2">
              <CaretDownIcon className="transition-transform group-data-[state=closed]:-rotate-90" />
              What was read
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent
            className={cn(COLLAPSIBLE_MOTION, 'min-w-0 max-w-full space-y-3 pt-2')}
          >
            {report.sources.length ? <WhatWasRead sources={report.sources} /> : null}
            {report.limitations.map((limitation) => (
              <p className={cn(ITEM_META, 'break-words')} key={limitation}>
                {limitation}
              </p>
            ))}
          </CollapsibleContent>
        </Collapsible>
      ) : null}
    </section>
  );
}
